/* Import components */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DataTablePaginated } from '../components';
import { axios } from '../libs/axios';
import { getProjectSocket } from '../libs/socket';
import ReactPaginate from 'react-paginate';
import { useSelector } from 'react-redux';
import { User } from '../states/actions';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n/I18nContext';
import logger from '../utils/logger';
import { generateRolling12Months, parsePeriodValue } from '../utils/periodFilters';
import {
	capitalizeName,
	validateCompanyRegCode,
	validateEmail,
	validateName,
	validatePID,
	validatePhone,
} from '../utils/clientValidation';

const BLANK_NEW_CLIENT = {
	firstname: '',
	lastname: '',
	email: '',
	phone: '',
	pid: '',
	type: 'person',
	business_name: '',
	business_address: '',
	business_reg_code: '',
};


/* Format date */

function formatDateDMY(date) {
	if (!date) return '';
	const d = new Date(date);
	const day = d.getDate().toString().padStart(2, '0');
	const month = (d.getMonth() + 1).toString().padStart(2, '0');
	const year = d.getFullYear();
	return `${day}.${month}.${year}`;
}

/* Convert line breaks to HTML */

function convertLineBreaksToHtml(text) {
	if (!text) return '';
	return text.replace(/\n/g, '<br>');
}

const PROJECT_STORAGE_KEYS = {
	status: 'hugo.projects.status',
	projectType: 'hugo.projects.projectType',
	month: 'hugo.projects.month',
	year: 'hugo.projects.year',
	partner: 'hugo.projects.partner',
};

function readStoredProjectFilter(key, fallback) {
	try {
		const value = localStorage.getItem(key);
		return value !== null && value !== '' ? value : fallback;
	} catch {
		return fallback;
	}
}

/* Status filter */

const StatusFilter = ({ statusFilter, onChange, statusCounts, userRole, t }) => {
	const baseOpts = [
		{ label: t('common.all'),      value:'all',     },
		{ label: t('common.statusWaiting'),  value:'waiting', },
		{ label: t('common.statusActive'),   value:'active',  },
		{ label: t('common.statusFinished'), value:'finished' }
	];
	
	// Add Cancelled option only for admin users
	const opts = userRole === 'admin' 
		? [...baseOpts, { label: t('common.statusCancelled'), value:'cancelled' }]
		: baseOpts;
	
	return (
		<>
			{opts.map(o => {
				const sel = statusFilter === o.value;
				const count = statusCounts[o.value] || 0;
				return (
					<div
						key={o.value}
						onClick={() => onChange(o.value)}
						className={`status status-${o.value}${sel ? ' selected' : ''}`}
						style={{whiteSpace: 'nowrap'}}>
						{o.value !== 'all' && (<span className="indicator"/>)}
						{o.label} ({count})
					</div>
				);
			})}
		</>
	);
};

/* Export projects */

export const Projects = () => {
	const user = useSelector(User.get());
	const { t, locale } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const [projects,setProjects]         = useState([]);
	const [rawProjects, setRawProjects]  = useState([]);
	const [selectedProjects,setSelected] = useState([]);
	const [loading,setLoading]           = useState(false);
	const [currentPage,setCurrentPage]   = useState(1);
	const [totalRecords,setTotalRecords] = useState(0);
	const [sizePerPage,setSizePerPage]   = useState(100);
	const [statusFilter,setStatusFilter] = useState(() => readStoredProjectFilter(PROJECT_STORAGE_KEYS.status, 'all'));
	const [projectTypeFilter, setProjectTypeFilter] = useState(() => readStoredProjectFilter(PROJECT_STORAGE_KEYS.projectType, 'all'));
	const [month, setMonth]               = useState(() => readStoredProjectFilter(PROJECT_STORAGE_KEYS.month, ''));
	const [year, setYear]                 = useState(() => readStoredProjectFilter(PROJECT_STORAGE_KEYS.year, ''));
	const [partnerFilter, setPartnerFilter] = useState(() => readStoredProjectFilter(PROJECT_STORAGE_KEYS.partner, ''));
	const [tooltip,setTooltip]           = useState({visible:false,text:'',x:0,y:0});
	const [search, setSearch]            = useState('');
	const [sortField, setSortField]      = useState('updatedAt');
	const [sortOrder, setSortOrder]      = useState('desc');
	const [syncingProjects, setSyncingProjects] = useState(false);
	const [projectSheetGid, setProjectSheetGid] = useState(null);
	const [spreadsheetUrl, setSpreadsheetUrl] = useState(null);

	// Only run URL sync when we are exactly on the list path (not /projects/123)
	// Also include home path (/) since it renders the same Projects component
	const isProjectsListPage = location.pathname === '/projects' || location.pathname === '/';

	// Skip first state->URL replace so we don't overwrite restored query params with default state (prevents loop and losing filters on Back)
	const skipNextReplaceRef = useRef(true);
	const joinedRoomsRef = useRef([]);

	// Flag to indicate URL state has been restored from query params
	const [urlRestored, setUrlRestored] = useState(false);

	// Sync URL query -> state (on mount and when user clicks Back)
	// Use useLayoutEffect to ensure this runs synchronously before browser paint
	useLayoutEffect(() => {
		if (!isProjectsListPage) {
			setUrlRestored(false); // Reset if not on the list page
			return;
		}
		const params = new URLSearchParams(location.search);
		if (params.has('status')) setStatusFilter(params.get('status') || 'all');
		if (params.has('project_type')) setProjectTypeFilter(params.get('project_type') || 'all');
		if (params.has('month')) setMonth(params.get('month') || '');
		if (params.has('year')) setYear(params.get('year') || '');
		if (params.has('partner')) setPartnerFilter(params.get('partner') || '');
		if (params.has('q')) setSearch(params.get('q') || '');
		if (params.has('page')) setCurrentPage(parseInt(params.get('page'), 10) || 1);
		if (params.has('sizePerPage')) setSizePerPage(parseInt(params.get('sizePerPage'), 10) || 100);
		if (params.has('sortField')) setSortField(params.get('sortField') || 'updatedAt');
		if (params.has('sortOrder')) setSortOrder(params.get('sortOrder') || 'desc');
		setUrlRestored(true); // Signal that URL state has been applied
	}, [location.pathname, location.search, isProjectsListPage]);

	// Sync state -> URL when filters or page change (so Back button restores them). Skip replace on first run only when URL already has params (Back restore), so we don't overwrite and loop.
	useEffect(() => {
		if (!isProjectsListPage) return;
		if (skipNextReplaceRef.current) {
			skipNextReplaceRef.current = false;
			if (location.search) return; // Keep restored query params; don't replace
		}
		const params = new URLSearchParams();
		params.set('status', statusFilter);
		params.set('project_type', projectTypeFilter);
		params.set('month', month);
		params.set('year', year);
		params.set('partner', partnerFilter);
		params.set('q', search);
		params.set('page', String(currentPage));
		params.set('sizePerPage', String(sizePerPage));
		params.set('sortField', sortField);
		params.set('sortOrder', sortOrder);
		const searchString = params.toString();
		const newSearch = searchString ? '?' + searchString : '';
		if (location.search !== newSearch) {
			// Preserve the current pathname when replacing URL (so / stays /, /projects stays /projects)
			history.replace({ pathname: location.pathname, search: newSearch });
		}
	}, [statusFilter, projectTypeFilter, month, year, partnerFilter, search, currentPage, sizePerPage, sortField, sortOrder, isProjectsListPage, location.pathname]);

	useEffect(() => {
		try {
			localStorage.setItem(PROJECT_STORAGE_KEYS.status, statusFilter);
		} catch {
			// ignore storage errors
		}
	}, [statusFilter]);

	useEffect(() => {
		try {
			localStorage.setItem(PROJECT_STORAGE_KEYS.projectType, projectTypeFilter);
		} catch {
			// ignore storage errors
		}
	}, [projectTypeFilter]);

	useEffect(() => {
		try {
			if (month) {
				localStorage.setItem(PROJECT_STORAGE_KEYS.month, month);
			} else {
				localStorage.removeItem(PROJECT_STORAGE_KEYS.month);
			}
		} catch {
			// ignore storage errors
		}
	}, [month]);

	useEffect(() => {
		try {
			if (year) {
				localStorage.setItem(PROJECT_STORAGE_KEYS.year, year);
			} else {
				localStorage.removeItem(PROJECT_STORAGE_KEYS.year);
			}
		} catch {
			// ignore storage errors
		}
	}, [year]);

	useEffect(() => {
		try {
			if (partnerFilter) {
				localStorage.setItem(PROJECT_STORAGE_KEYS.partner, partnerFilter);
			} else {
				localStorage.removeItem(PROJECT_STORAGE_KEYS.partner);
			}
		} catch {
			// ignore storage errors
		}
	}, [partnerFilter]);

	// Fetch spreadsheet URL from settings on mount
	useEffect(() => {
		const fetchSpreadsheetUrl = async () => {
			try {
				const response = await axios.get('/crm/settings');
				const data = response.data || response;
				if (data.spreadsheetUrl) {
					setSpreadsheetUrl(data.spreadsheetUrl);
				} else if (data.spreadsheetId) {
					// Fallback: construct URL from ID if URL not available
					setSpreadsheetUrl(`https://docs.google.com/spreadsheets/d/${data.spreadsheetId}/edit`);
				}
			} catch (error) {
				logger.error('Error fetching spreadsheet URL:', error);
			}
		};
		fetchSpreadsheetUrl();
	}, []);

	const [showProjectModal, setShowProjectModal] = useState(false);
	const [editingProjectId, setEditingProjectId] = useState(null);
	const [projectForm, setProjectForm] = useState({ project_owner: '', project_owner_id: '' });
	
	/* New project modal state */
	const [showNewProjectModal, setShowNewProjectModal] = useState(false);
	const [newProjectForm, setNewProjectForm] = useState({
		project_name: '',
		project_description: '',
		project_type: 'General Booking',
		related_client_id: '',
		client_name: '',
		business_name: '',
		client_type: 'person',
		status: 'waiting'
	});
		const [clients, setClients] = useState([]);
	const [crmUsers, setCrmUsers] = useState([]); // Add CRM users state for partner filter
	const [isCreating, setIsCreating] = useState(false);
	
	// Status counts state - keeps track of total counts per status
	const [statusCounts, setStatusCounts] = useState({
		all: 0,
		waiting: 0,
		active: 0,
		finished: 0,
		cancelled: 0
	});

	// Totals state - always from list response (Totals button approach commented out)
	const [totals, setTotals] = useState({ total_billed: 0, total_unbilled: 0, total_retainer: 0, total_balance: 0 });
	// const [totalsLoading, setTotalsLoading] = useState(false);

	/* Client search functionality */
	const [clientSearch, setClientSearch] = useState('');
	const [filteredClients, setFilteredClients] = useState([]);
	const [showClientDropdown, setShowClientDropdown] = useState(false);

	/* Lawyer new-project client flow */
	const [lawyerClientMode, setLawyerClientMode] = useState('existing');
	const [newClientForm, setNewClientForm] = useState(BLANK_NEW_CLIENT);
	const [newClientErrors, setNewClientErrors] = useState({
		name: '',
		email: '',
		phone: '',
		pid: '',
		business: '',
		general: '',
	});
	const [linkPrompt, setLinkPrompt] = useState(null);
	const [pendingProjectSubmit, setPendingProjectSubmit] = useState(false);

	/* Owner change functionality for admin users */
	const [ownerChangeLoading, setOwnerChangeLoading] = useState(false);
	
	/* Fetch projects */
	
	const fetchProjects = async(page=1, size=100, q='', status='all', projectType='all', month='', year='', partner='', sortField='updatedAt', sortOrder='desc')=> {
		setLoading(true);
		try {
			// Parse period if month contains hyphen (YYYY-M format for "All Years" mode)
			let yearParam = year;
			let monthParam = month;
			if (month && month.includes('-')) {
				const { year: y, month: m } = parsePeriodValue(month);
				yearParam = y;
				monthParam = m;
			}

			const {projects: rows, total, totals: responseT} = await axios.get('/projects', {
				params: {
					page,
					sizePerPage: size,
					q,
					status,
					project_type: projectType,
					month: monthParam,
					year: yearParam,
					partner,
					sortField,
					sortOrder,
				}
			});
			setRawProjects(rows);
			setProjects(rows);
			setTotalRecords(total);
			setTotals(responseT || { total_billed: 0, total_unbilled: 0, total_retainer: 0, total_balance: 0 });
			setSelected([]);
		} finally { setLoading(false); }
	};

	// Totals button approach commented out - totals always come with list response
	// const fetchTotals = async () => {
	// 	setTotalsLoading(true);
	// 	try {
	// 		const body = await axios.get('/projects/totals', {
	// 			params: { q: search, status: statusFilter, project_type: projectTypeFilter, month, year, partner: partnerFilter },
	// 		});
	// 		if (body?.status && body.status >= 400) return;
	// 		setTotals(body?.totals ?? null);
	// 	} catch (err) {
	// 		logger.error('Error fetching totals:', err);
	// 	} finally {
	// 		setTotalsLoading(false);
	// 	}
	// };

	const handleSyncProjectsToSheets = async () => {
		try {
			setSyncingProjects(true);
			const response = await axios.post('/projects/sync-projects-to-sheets');
			
			// Handle response - axios might return data directly or nested
			const responseData = response.data || response;
			const message = responseData?.message || t('projects.syncSuccess');
			
			// Store the spreadsheet URL for direct link
			if (responseData?.spreadsheetUrl) {
				setSpreadsheetUrl(responseData.spreadsheetUrl);
			} else if (responseData?.spreadsheetId) {
				// Fallback: construct URL from ID if URL not available
				setSpreadsheetUrl(`https://docs.google.com/spreadsheets/d/${responseData.spreadsheetId}/edit`);
			}
			if (responseData?.sheetGid) {
				setProjectSheetGid(responseData.sheetGid);
			}
			
			window.nSuccess({
				title: t('common.successTitle'),
				text: message,
				icon: 'fa-check'
			});
		} catch (error) {
			logger.error('Error syncing projects to sheets:', error);
			const errorMessage = error?.response?.data?.message || error?.message || t('projects.syncErrorText');
			window.nError({
				title: t('common.syncError'),
				text: errorMessage,
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setSyncingProjects(false);
		}
	};
	
	/* Fetch status counts - independent of current filter */
	
	const fetchStatusCounts = async() => {
		try {
			// Parse period if month contains hyphen (YYYY-M format for "All Years" mode)
			let yearParam = year;
			let monthParam = month;
			if (month && month.includes('-')) {
				const { year: y, month: m } = parsePeriodValue(month);
				yearParam = y;
				monthParam = m;
			}
			
			// Fetch status counts from dedicated endpoint (much lighter than fetching all projects)
			const response = await axios.get('/projects/status-counts', {
				params: {
					q: search,
					project_type: projectTypeFilter,
					month: monthParam,
					year: yearParam,
					partner: partnerFilter,
				}
			});
			
			setStatusCounts(response.statusCounts);
		} catch (error) {
			logger.error('Error fetching status counts:', error);
		}
	};

	/* Handle table sorting */
	
	const handleTableChange = (type, { sortField, sortOrder }) => {
		if (type === 'sort') {
			setSortField(sortField);
			setSortOrder(sortOrder);
			setCurrentPage(1); // Reset to first page when sorting
		}
	};
	
	/* Fetch available clients for dropdown */
	const fetchAvailableClients = async() => {
		try {
			if (user?.role === 'lawyer') {
				const { clients: myClients } = await axios.get('/clients/my-clients');
				const normalized = (myClients || []).map((c) => ({
					_id: c._id,
					display_name: c.display_name,
					firstname: c.display_name,
					lastname: '',
					type: c.type || 'person',
					business_name: c.type === 'business' ? c.display_name : '',
				}));
				setClients(normalized);
				setFilteredClients(normalized);
				return;
			}
			const { clients } = await axios.get('/clients', {
				params: { sizePerPage: 10000 },
			});
			setClients(clients);
			setFilteredClients(clients);
		} catch (error) {
			logger.error('Error fetching clients:', error);
		}
	};

	const resetLawyerClientState = () => {
		setLawyerClientMode('existing');
		setNewClientForm(BLANK_NEW_CLIENT);
		setNewClientErrors({ name: '', email: '', phone: '', pid: '', business: '', general: '' });
		setLinkPrompt(null);
		setPendingProjectSubmit(false);
	};

	const lawyerClientFromPreview = (previewClient) => ({
		_id: previewClient._id,
		display_name: previewClient.display_name,
		firstname: previewClient.display_name,
		lastname: '',
		type: 'person',
	});

	const previewLinkByEmail = async (email) => {
		if (user?.role !== 'lawyer' || !email?.trim()) return null;
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return null;
		const trimmedEmail = email.trim();
		try {
			const preview = await axios.post('/clients/link-existing', { email: trimmedEmail });
			if (preview?.status === 404 || !preview?.success) return null;
			if (preview.already_linked) {
				selectClient(lawyerClientFromPreview(preview.client));
				setLawyerClientMode('existing');
				setLinkPrompt(null);
				return { status: 'ready', clientId: preview.client._id };
			}
			setLinkPrompt({
				email: trimmedEmail,
				displayName: preview.client.display_name,
				clientId: preview.client._id,
			});
			return { status: 'prompt' };
		} catch (error) {
			logger.error('Error previewing client link by email:', error);
			return null;
		}
	};

	const confirmLinkClient = async () => {
		if (!linkPrompt?.email) return null;
		const linked = await axios.post('/clients/link-existing', {
			email: linkPrompt.email,
			confirm: true,
		});
		if (linked?.success) {
			await fetchAvailableClients();
			selectClient(lawyerClientFromPreview(linked.client));
			setLawyerClientMode('existing');
			setLinkPrompt(null);
			return linked.client._id;
		}
		return null;
	};

	const cancelLinkPrompt = () => {
		setLinkPrompt(null);
		setPendingProjectSubmit(false);
	};

	const switchToNewClientMode = () => {
		setLawyerClientMode('new');
		setLinkPrompt(null);
		setPendingProjectSubmit(false);
		clearClientSelection();
		setShowClientDropdown(false);
	};

	const validateLawyerNewClientForm = () => {
		const errors = { name: '', email: '', phone: '', pid: '', business: '', general: '' };
		const firstNameResult = validateName(newClientForm.firstname, t);
		const lastNameResult = validateName(newClientForm.lastname, t);
		if (!firstNameResult.isValid || !lastNameResult.isValid) {
			errors.name = firstNameResult.error || lastNameResult.error;
		}
		const emailResult = validateEmail(newClientForm.email, t);
		if (!emailResult.isValid) errors.email = emailResult.error;
		const phoneResult = validatePhone(newClientForm.phone, t);
		if (!phoneResult.isValid) errors.phone = phoneResult.error;
		const pidResult = validatePID(newClientForm.pid, t);
		if (!pidResult.isValid) errors.pid = pidResult.error;
		if (newClientForm.type === 'business') {
			if (!newClientForm.business_name?.trim() || !newClientForm.business_address?.trim() || !newClientForm.business_reg_code?.trim()) {
				errors.business = t('projects.clientBusinessRequired');
			} else {
				const regResult = validateCompanyRegCode(newClientForm.business_reg_code, t);
				if (!regResult.isValid) errors.business = regResult.error;
			}
		}
		setNewClientErrors(errors);
		return !Object.values(errors).some(Boolean);
	};

	const createProjectClient = async () => {
		const trimmedForm = {
			...newClientForm,
			firstname: capitalizeName(newClientForm.firstname?.trim() || ''),
			lastname: capitalizeName(newClientForm.lastname?.trim() || ''),
			email: newClientForm.email?.trim() || '',
			phone: newClientForm.phone?.trim() || '',
			pid: newClientForm.pid?.trim() || '',
			business_name: newClientForm.business_name?.trim() || '',
			business_address: newClientForm.business_address?.trim() || '',
			business_reg_code: newClientForm.business_reg_code?.trim() || '',
		};
		setNewClientForm(trimmedForm);

		if (user.role === 'lawyer') {
			const linkResult = await previewLinkByEmail(trimmedForm.email);
			if (linkResult?.status === 'prompt') {
				setPendingProjectSubmit(true);
				return null;
			}
			if (linkResult?.status === 'ready') {
				return linkResult.clientId;
			}
		}

		const result = await axios.post('/clients', trimmedForm);
		if (result?.status === 409) {
			if (user.role === 'lawyer' && result.clientExists && result.existingClientId) {
				const linkResult = await previewLinkByEmail(trimmedForm.email);
				if (linkResult?.status === 'ready') {
					return linkResult.clientId;
				}
				if (linkResult?.status === 'prompt') {
					setPendingProjectSubmit(true);
				}
				return null;
			}
			if (result.validationErrors) {
				setNewClientErrors((prev) => ({ ...prev, general: t('projects.clientDuplicateGeneric') }));
				return null;
			}
			setNewClientErrors((prev) => ({
				...prev,
				general: result.error || result.message || t('projects.createClientError'),
			}));
			return null;
		}
		if (result?.status && result.status >= 400) {
			setNewClientErrors((prev) => ({
				...prev,
				general: result.error || result.message || t('projects.createClientError'),
			}));
			return null;
		}
		const clientId = result?.client?._id;
		if (clientId) {
			await fetchAvailableClients();
		}
		return clientId || null;
	};

	const handleConfirmLinkAndContinue = async () => {
		try {
			const clientId = await confirmLinkClient();
			if (!clientId) return;
			setPendingProjectSubmit(false);
			if (isCreating) {
				await submitProjectWithClientId(clientId);
			} else {
				window.nSuccess({ title: t('common.successTitle'), text: t('projects.linkClientSuccess'), icon: 'fa-check-square' });
			}
		} catch (error) {
			logger.error('Error linking client:', error);
			window.nError({ title: t('common.errorTitle'), text: t('projects.linkClientError'), icon: 'fa-exclamation-triangle' });
		}
	};

	/* Client search functionality */
	const handleClientSearch = (searchTerm) => {
		setClientSearch(searchTerm);
		
		if (searchTerm.trim() === '') {
			setFilteredClients(clients);
			setShowClientDropdown(false);
		} else {
			const searchLower = searchTerm.toLowerCase();
			
			// Filter and score clients based on relevance
			const filtered = clients.filter(client => {
				if (user?.role === 'lawyer') {
					const name = (client.display_name || `${client.firstname} ${client.lastname}`).toLowerCase();
					return name.includes(searchLower);
				}
				const fullName = `${client.firstname} ${client.lastname}`.toLowerCase();
				const email = (client.email || '').toLowerCase();
				const businessName = client.business_name ? client.business_name.toLowerCase() : '';
				
				return fullName.includes(searchLower) || 
					   email.includes(searchLower) || 
					   businessName.includes(searchLower);
			}).sort((a, b) => {
				// Prioritize business name matches for business clients
				if (a.type === 'business' && a.business_name && a.business_name.toLowerCase().includes(searchLower)) {
					if (b.type !== 'business' || !b.business_name || !b.business_name.toLowerCase().includes(searchLower)) {
						return -1; // a comes first
					}
				}
				if (b.type === 'business' && b.business_name && b.business_name.toLowerCase().includes(searchLower)) {
					if (a.type !== 'business' || !a.business_name || !a.business_name.toLowerCase().includes(searchLower)) {
						return 1; // b comes first
					}
				}
				
				// Then prioritize exact name matches
				const aFullName = `${a.firstname} ${a.lastname}`.toLowerCase();
				const bFullName = `${b.firstname} ${b.lastname}`.toLowerCase();
				
				if (aFullName.startsWith(searchLower) && !bFullName.startsWith(searchLower)) return -1;
				if (bFullName.startsWith(searchLower) && !aFullName.startsWith(searchLower)) return 1;
				
				// Finally sort alphabetically
				return aFullName.localeCompare(bFullName);
			});
			
			setFilteredClients(filtered);
			setShowClientDropdown(true);
		}
	};

	const selectClient = (client) => {
		const isBusiness = client.type === 'business';
		const representativeName = client.display_name || `${client.firstname} ${client.lastname}`.trim();
		setNewProjectForm({
			...newProjectForm,
			related_client_id: client._id,
			client_name: representativeName,
			business_name: client.business_name || '',
			client_type: client.type || 'person'
		});
		
		const displayName = user?.role === 'lawyer'
			? representativeName
			: (isBusiness
				? `${client.business_name} ${t('projects.businessSuffix')}`
				: `${client.firstname} ${client.lastname} (${client.email})`);
		setClientSearch(displayName);
		setShowClientDropdown(false);
	};

	const clearClientSelection = () => {
		setNewProjectForm({
			...newProjectForm,
			related_client_id: '',
			client_name: '',
			business_name: '',
			client_type: 'person'
		});
		setClientSearch('');
		setShowClientDropdown(false);
	};
	
	/* Fetch CRM users for admin owner change */
	const fetchCrmUsers = async() => {
		if (user && user.role === 'admin') {
			try {
				setOwnerChangeLoading(true);
				const users = await axios.get('/projects/crm-users/all');
				setCrmUsers(users);
			} catch (error) {
				logger.error('Error fetching CRM users:', error);
			} finally {
				setOwnerChangeLoading(false);
			}
		}
	};
	
	useEffect(()=> { 
		if (!urlRestored || !isProjectsListPage) return; // Gate fetch until URL state is restored
		fetchProjects(currentPage, sizePerPage, search, statusFilter, projectTypeFilter, month, year, partnerFilter, sortField, sortOrder); 
		fetchAvailableClients();
		fetchCrmUsers();
	}, [currentPage, sizePerPage, statusFilter, projectTypeFilter, search, month, year, partnerFilter, sortField, sortOrder, urlRestored, isProjectsListPage]);
	
	// Fetch status counts whenever filters change (but not status filter itself)
	useEffect(() => {
		if (!urlRestored || !isProjectsListPage) return; // Gate until URL state is restored
		fetchStatusCounts();
	}, [search, month, year, partnerFilter, projectTypeFilter, urlRestored, isProjectsListPage]);

	// Close client dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest('.client-search-container')) {
				setShowClientDropdown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);
	
	/* Search fallback */
	
	useEffect(() => {
		const s = search.trim().toLowerCase();
		setProjects(s ? rawProjects.filter(p => {
			const createdDate  = formatDateDMY(p.createdAt).toLowerCase();
			const deadlineDate = (formatDateDMY(p.deadline_earliest) || '').toLowerCase();

			return deadlineDate.includes(s)
				|| p.project_name.toLowerCase().includes(s)
				|| p.project_no.toLowerCase().includes(s)
				|| p.client_name.toLowerCase().includes(s)
				|| (p.business_name && p.business_name.toLowerCase().includes(s))
				|| (p.project_owner && p.project_owner.toLowerCase().includes(s))
				|| createdDate.includes(s);
		}) : rawProjects);
	}, [search, rawProjects]);

	const projectRoomIdsKey = useMemo(
		() => (Array.isArray(projects) ? projects : []).map((p) => String(p._id)).join(','),
		[projects],
	);

	/* Join Socket.IO rooms for visible rows only; bump unread on client new_message (no extra DB). */
	useEffect(() => {
		if (!isProjectsListPage) {
			return undefined;
		}
		const socket = getProjectSocket();
		socket.connect();

		const handler = (payload) => {
			if (payload?.sender_role !== 'client' || !payload?.project_id) {
				return;
			}
			const pid = String(payload.project_id);
			const incRow = (row) =>
				String(row._id) === pid
					? { ...row, client_unread_count: (Number(row.client_unread_count) || 0) + 1 }
					: row;
			setRawProjects((prev) => (Array.isArray(prev) ? prev.map(incRow) : prev));
			setProjects((prev) => (Array.isArray(prev) ? prev.map(incRow) : prev));
		};

		joinedRoomsRef.current.forEach((rid) => socket.emit('leave_project', rid));
		const ids = projectRoomIdsKey ? projectRoomIdsKey.split(',').filter(Boolean) : [];
		ids.forEach((rid) => socket.emit('join_project', rid));
		joinedRoomsRef.current = ids;

		socket.on('new_message', handler);
		return () => {
			socket.off('new_message', handler);
			joinedRoomsRef.current.forEach((rid) => socket.emit('leave_project', rid));
			joinedRoomsRef.current = [];
			socket.disconnect();
		};
	}, [projectRoomIdsKey, isProjectsListPage]);

	/* Delete project */
	
	const confirmDelete = (text, onYes)=> {
		let n; n = window.nConfirm({
			title: t('projects.deleteConfirm'),
			text,
			icon:'fa-exclamation-circle',
			onYes: async()=> { await onYes(); n?.close(); },
			onNo : ()=> { n?.close(); }
		});
	};
	
	const deleteProject = (id, projectName, clientName)=> {
		const confirmText = `<div style="text-align: left; margin-top: 12px; margin-bottom: 16px;"><strong>${projectName}</strong><br/>${t('projects.deleteConfirmClient')} ${clientName}</div>`;
		confirmDelete(confirmText, async()=> {
			await axios.delete('/projects/bulk',{ projectIds:[id] });
			fetchProjects(currentPage, sizePerPage, search, statusFilter, projectTypeFilter, month, year, partnerFilter);
		});
	};
	
	const handleProjectUpdate = async (e) => {
		e.preventDefault();
		try {
		await axios.patch(`/projects/${editingProjectId}`, projectForm);
		await fetchProjects(currentPage, sizePerPage, search, statusFilter, month, year, partnerFilter);
		setShowProjectModal(false);
		} catch (error) {
			logger.error('Error updating project:', error);
		}
	};
	
	/* New project handlers */
	const openNewProject = () => {
		// Set project type based on user role
		const projectType = user && user.role === 'admin' ? 'admin' : 
						   user && user.role === 'lawyer' ? 'lawyer' : 'General Booking';
		
		setNewProjectForm({
			project_name: '',
			project_description: '',
			project_type: projectType,
			related_client_id: '',
			client_name: '',
			business_name: '',
			client_type: 'person',
			status: 'waiting',
			// Initialize project owner to current user for admin users only
			...(user.role === 'admin' && {
				project_owner: user.fullName || `${user.firstName} ${user.lastName}`,
				project_owner_id: user.user_no
			})
		});
		setClientSearch('');
		setShowClientDropdown(false);
		resetLawyerClientState();
		setIsCreating(true);
		setShowNewProjectModal(true);
	};
	
	const handleClientChange = (clientId) => {
		const selectedClient = clients.find(c => c._id === clientId);
		if (selectedClient) {
		setNewProjectForm({
			...newProjectForm,
			related_client_id: clientId,
			client_name: `${selectedClient.firstname} ${selectedClient.lastname}`,
			business_name: selectedClient.business_name || '',
			client_type: selectedClient.type || 'person'
		});
		}
	};
	
	const submitProjectWithClientId = async (clientIdOverride) => {
		let finalProjectOwnerUserNo = user.user_no;
		if (user.role === 'admin' && newProjectForm.project_owner_id) {
			finalProjectOwnerUserNo = newProjectForm.project_owner_id;
		}

		const projectData = {
			...newProjectForm,
			related_client_id: clientIdOverride || newProjectForm.related_client_id,
			project_owner: user.role === 'admin' && newProjectForm.project_owner
				? newProjectForm.project_owner
				: (user.fullName || `${user.firstName} ${user.lastName}`),
			project_owner_id: finalProjectOwnerUserNo,
		};

		if ((user.role === 'lawyer' || user.role === 'admin') && lawyerClientMode === 'new' && (clientIdOverride || newProjectForm.related_client_id)) {
			projectData.client_name = newClientForm.type === 'business'
				? newClientForm.business_name
				: `${newClientForm.firstname} ${newClientForm.lastname}`.trim();
			projectData.client_type = newClientForm.type;
			projectData.business_name = newClientForm.type === 'business' ? newClientForm.business_name : '';
		}

		if (user.role === 'lawyer' || projectData.project_type === 'lawyer') {
			projectData.status = 'active';
		}

		await axios.post('/projects/create', projectData);
		window.nSuccess({ title: t('common.successTitle'), text: t('projects.createSuccess'), icon: 'fa-check-square' });
		setShowNewProjectModal(false);
		setClientSearch('');
		setShowClientDropdown(false);
		resetLawyerClientState();
		fetchProjects(currentPage, sizePerPage, search, statusFilter, projectTypeFilter, month, year, partnerFilter);
	};

	const handleCreateProject = async (e) => {
		e.preventDefault();
		try {
			if (isCreating) {
				if (user.role === 'lawyer' || user.role === 'admin') {
					if (user.role === 'lawyer' && linkPrompt) {
						return;
					}

					let clientId = newProjectForm.related_client_id;

					if (lawyerClientMode === 'new') {
						if (!validateLawyerNewClientForm()) return;
						clientId = await createProjectClient();
						if (!clientId) return;
					} else if (!clientId) {
						if (user.role === 'lawyer') {
							const linkResult = await previewLinkByEmail(clientSearch);
							if (linkResult?.status === 'prompt') {
								setPendingProjectSubmit(true);
								return;
							}
							if (linkResult?.status === 'ready') {
								clientId = linkResult.clientId;
							}
						}
						window.nError({
							title: t('common.errorTitle'),
							text: t('projects.selectClientRequired'),
							icon: 'fa-exclamation-triangle',
						});
						return;
					}

					await submitProjectWithClientId(clientId);
					return;
				}

				await submitProjectWithClientId(newProjectForm.related_client_id);
			} else {
				await axios.patch(`/projects/${editingProjectId}`, newProjectForm);
				window.nSuccess({ title: t('common.successTitle'), text: t('projects.updateSuccess'), icon: 'fa-check-square' });
				setShowNewProjectModal(false);
				setClientSearch('');
				setShowClientDropdown(false);
				fetchProjects(currentPage, sizePerPage, search, statusFilter, projectTypeFilter, month, year, partnerFilter);
			}
		} catch (error) {
			logger.error('Error creating/updating project:', error);
			window.nError({ title: t('common.errorTitle'), text: isCreating ? t('projects.createError') : t('projects.updateError'), icon: 'fa-exclamation-triangle' });
		}
	};
	
	
	/* Handle CSV download */
	
	const handleDownloadCSV = async () => {
		try {
			logger.log('Starting CSV download with params:', { month, year, search, partnerFilter, statusFilter, projectTypeFilter, sortField, sortOrder });
			
			// Parse period if month contains hyphen (YYYY-M format for "All Years" mode)
			let yearParam = year;
			let monthParam = month;
			if (month && month.includes('-')) {
				const { year: y, month: m } = parsePeriodValue(month);
				yearParam = y;
				monthParam = m;
			}
			
			let response;
			
			try {
				// Use axios with text response first (more reliable)
				logger.log('Making axios request to /projects/download/csv');
				response = await axios.get('/projects/download/csv', {
					params: { 
						month: monthParam, 
						year: yearParam,
						q: search,
						project_type: projectTypeFilter,
						partner: partnerFilter,
						status: statusFilter,
						sortField,
						sortOrder
					},
					responseType: 'text',
				});
				
				logger.log('Raw axios response:', response);
				logger.log('Response type:', typeof response);
				logger.log('Response properties:', Object.keys(response));
				logger.log('Response data type:', typeof response.data);
				
				// Handle different response structures
				let csvData;
				if (typeof response === 'string') {
					// Response is directly the CSV string
					csvData = response;
					logger.log('Response is directly CSV string, length:', csvData.length);
				} else if (response.data) {
					// Response has data property
					csvData = response.data;
					logger.log('Response data length:', csvData.length);
				} else {
					throw new Error('No CSV data found in response');
				}
				
				// Create blob from CSV data
				const blob = new Blob([csvData], { type: 'text/csv' });
				logger.log('Created blob from CSV data:', blob);
				
				// Create download link
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;

				// Create filename based on current filters
				let filename = '';
				let filenameParts = [];
				
				// Add month if selected
				if (month) {
					const monthName = new Date(0, month - 1).toLocaleString('default', { month: 'long' }).toLowerCase();
					filenameParts.push(monthName);
				}
				
				// Add year if selected
				if (year) {
					filenameParts.push(year.toString());
				}
				
				// Add partner if selected
				if (partnerFilter) {
					const selectedPartner = crmUsers.find(p => p.user_no === partnerFilter);
					if (selectedPartner) {
						const partnerName = selectedPartner.firstName && selectedPartner.lastName 
							? `${selectedPartner.firstName}-${selectedPartner.lastName}`.toLowerCase().replace(/\s+/g, '-')
							: 'partner';
						filenameParts.push(partnerName);
					}
				}
				
				// Add status if not 'all'
				if (statusFilter && statusFilter !== 'all') {
					filenameParts.push(statusFilter);
				}
				
				// Build final filename
				if (filenameParts.length > 0) {
					filename = `${filenameParts.join('-')}-projects-hugo.csv`;
				} else {
					filename = 'projects-hugo.csv';
				}
				
				link.download = filename;
				logger.log('Download filename:', filename);
				
				// Trigger download
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				
				// Clean up
				window.URL.revokeObjectURL(url);
				
				logger.log('CSV download completed successfully');
				
			} catch (axiosError) {
				logger.error('Axios request failed:', axiosError);
				throw axiosError;
			}
			
		} catch (error) {
			logger.error('CSV download failed:', error);
			window.nError && window.nError({
				title: t('common.downloadError'), 
				text: t('projects.downloadCsvError'), 
				icon: 'fa-exclamation-triangle'
			});
		}
	};

	/* Select rows */
	
	const handleSelectRow = id => setSelected(sp=>sp.includes(id)?sp.filter(x=>x!==id):[...sp,id]);
	const handleSelectAll = ()=> {setSelected(sp=>sp.length===projects.length?[]:projects.map(p=>p._id));};
	
	/* Render status */
	
	const renderStatusIcon = s => {
		let c = 'status status-';
		if (s === 'active') c += 'active';
		else if (s === 'waiting') c += 'waiting';
		else if (s === 'finished') c += 'finished';
		else if (s === 'cancelled') c += 'cancelled';
		return (
			<div className={c}>
				<span className="indicator" />
			</div>
		);
	};
	
	const getRowClass = r => {
		let c = '';
		if (r.status === 'waiting') c = 'row-waiting';
		else if (r.status === 'active') c = 'row-active';
		else if (r.status === 'finished') c = 'row-finished';
		else if (r.status === 'cancelled') c = 'row-cancelled';
		else c = 'row-default';
		// Light pink background when deadline has passed (same as unpaid invoices)
		if (r.deadline_earliest) {
			const deadline = new Date(r.deadline_earliest);
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			deadline.setHours(0, 0, 0, 0);
			if (deadline < today) c += ' invoice-unpaid';
		}
		return c;
	};
	
	const buildProjectTotalRowData = (totals) => {
		const formatDuration = (minutes) => {
			const m = minutes || 0;
			const hours = Math.floor(m / 60);
			const mins = Math.round(m % 60);
			return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
		};

		// Totals button branch commented out - totals always from list response
		// if (totals === null) { return { actions: <button>Totals</button>, ... }; }

		return {
			actions: <></>,
			status: <></>,
			deadline_earliest: <></>,
			project_name: <></>,
			project_type: <></>,
			project_owner: <></>,
			client_name: <></>,
			total_billed_minutes: <strong>{formatDuration(totals.total_billed)}</strong>,
			total_unbilled_minutes: <strong>{formatDuration(totals.total_unbilled)}</strong>,
			total_retainer: <strong>€{(totals.total_retainer || 0).toFixed(2)}</strong>,
			total_balance: <strong>€{(totals.total_balance || 0).toFixed(2)}</strong>,
			createdAt: <></>,
			updatedAt: <></>
		};
	};
	
	/* Table columns */
	
	const tableColumns = [
		// Actions column - first position with Open button for all users
		{
			dataField: 'actions',
			text: '',
			classes: 'cell-actions',
			headerClasses: 'cell-actions',
			sort: false,
			style: { width: '140px', minWidth: '140px', maxWidth: '160px' },
			headerStyle: { width: '140px', minWidth: '140px', maxWidth: '160px' },
			formatter: (c, r) => (
				<div className='actions' style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
					{Number(r.client_unread_count) > 0 ? (
						<span className="client-unread-bubble" aria-hidden="true">
							{r.client_unread_count > 99 ? '99+' : r.client_unread_count}
						</span>
					) : null}
					<button className="button button-small" onClick={e => {
						e.stopPropagation();
						history.push(`/projects/${r.project_no}`);
					}}>
						{t('projects.open')}
					</button>
				</div>
			)
		},
		{
			dataField: 'deadline_earliest',
			text: t('projects.deadline'),
			classes: 'cell-deadline',
			headerClasses: 'cell-deadline',
			formatter: (c, r) => {
				if (!r.deadline_earliest) return '';
				const deadline = new Date(r.deadline_earliest);
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				deadline.setHours(0, 0, 0, 0);
				const isPastDeadline = deadline < today;
				const dateStr = formatDateDMY(r.deadline_earliest);
				return isPastDeadline ? (
					<span className="red" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
						<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<circle cx="12" cy="12" r="10" />
							<path d="M12 7v6" strokeWidth="2" />
							<circle cx="12" cy="17" r="1.25" fill="currentColor" stroke="none" />
						</svg>
						{dateStr}
					</span>
				) : dateStr;
			},
			sortValue: (cell, row) => row.deadline_earliest ? new Date(row.deadline_earliest).getTime() : 0,
			sort: true
		},
		{
			dataField: 'project_name',
			text: t('projects.project'),
			classes: 'cell-project',
			headerClasses: 'cell-project',
			sort: false,
			formatter: (c,r)=>
				<div
					className='inner'
					onClick={e =>{e.stopPropagation(); history.push(`/projects/${r.project_no}`);}}
					style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
					>
					{renderStatusIcon(r.status)}
					{r.project_name}
				</div>
		},
		// Owner column - for admin and support users
		...(user && (user.role === 'admin' || user.role === 'support') ? [{
			dataField: 'project_owner',
			text: t('projects.owner'),
			classes: 'cell-owner',
			headerClasses: 'cell-owner',
			sort: false,
			formatter: (c, r) => (
				<div className='inner'>
					{r.project_owner || t('projects.noOwner')}
				</div>
			)
		}] : []),
		{
			dataField: 'project_type',
			text: t('projects.type'),
			classes: 'cell-type',
			headerClasses: 'cell-type',
			sort: false,
			formatter: (c, r) => (
				r.project_type ? (
				<div className="tags">
					<ul>
					<li className="type"><span>{r.project_type}</span></li>
					</ul>
				</div>
				) : null
			)
		},
		{
			dataField: 'client_name',
			text: t('projects.client'),
			classes: 'cell-client',
			headerClasses: 'cell-client',
			sort: false,
			formatter: (c,r)=> {
				// Hide client info for lawyer users when project is in waiting state
				if (user.role === 'lawyer' && r.status === 'waiting') {
					return (
						<div className='inner' style={{fontSize: '13px', color: '#858ebd'}}>
							{t('projects.startToView')}
						</div>
					);
				}
				
				const isBusiness = r.client_type === 'business';
				const displayName = isBusiness ? r.business_name : r.client_name;
				
				return (
					<div className='inner'>
						<div className={`client-info ${isBusiness ? 'business' : 'person'}`}>
							<span className="client-name">{displayName}</span>
							{isBusiness && <span className="client-type-badge">{t('common.business')}</span>}
						</div>
					</div>
				);
			}
		},
		{
			dataField: 'total_billed_minutes',
			text: t('projects.billed'),
			classes: 'cell-duration',
			headerClasses: 'cell-duration',
			formatter: (minutes) => {
				const m = minutes || 0;
				const hours = Math.floor(m / 60);
				const mins = Math.round(m % 60);
				return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
			},
			sortValue: (cell) => cell || 0
		},
		{
			dataField: 'total_unbilled_minutes',
			text: t('projects.unbilled'),
			classes: 'cell-duration',
			headerClasses: 'cell-duration',
			formatter: (minutes) => {
				const m = minutes || 0;
				const hours = Math.floor(m / 60);
				const mins = Math.round(m % 60);
				return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
			},
			sortValue: (cell) => cell || 0
		},
		{
			dataField: 'total_retainer',
			text: t('projects.retainer'),
			classes: 'cell-retainer',
			headerClasses: 'cell-retainer',
			formatter: (amount) => {
				return amount && amount > 0 ? `€${amount.toFixed(2)}` : '-';
			},
			sortValue: (cell) => cell || 0
		},
		{
			dataField: 'total_balance',
			text: t('projects.balance'),
			classes: 'cell-balance',
			headerClasses: 'cell-balance',
			formatter: (amount) => {
				return amount && amount > 0 ? `€${amount.toFixed(2)}` : '-';
			},
			sortValue: (cell) => cell || 0
		},
		{
			dataField: 'createdAt',
			text: t('projects.created'),
			classes: 'cell-created',
			headerClasses: 'cell-created',
			formatter: d => formatDateDMY(d),
		},
		{
			dataField: 'updatedAt',
			text: t('projects.updated'),
			classes: 'cell-updated',
			headerClasses: 'cell-updated',
			formatter: d => formatDateDMY(d),
		},
		// Edit and Delete actions column - only for admin users, placed at the end
		...(user && user.role === 'admin' ? [{
			dataField: 'admin_actions',
			text: '',
			classes: 'cell-actions',
			headerClasses: 'cell-actions',
			sort: false,
			style: { width: '200px', minWidth: '200px', maxWidth: '200px' },
			headerStyle: { width: '200px', minWidth: '200px', maxWidth: '200px' },
			formatter: (c, r) => (
				<div className='actions'>
					<button className="button button-small" onClick={e => {
						e.stopPropagation();
					// Open edit modal with full project data
					setNewProjectForm({
						project_name: r.project_name || '',
						project_description: r.project_description || '',
						project_type: (r.project_type === 'general' ? 'General Booking' : r.project_type) || 'General Booking',
						related_client_id: r.related_client_id || '',
						client_name: r.client_name || '',
						business_name: r.business_name || '',
						client_type: r.client_type || 'person',
						status: r.status || 'waiting',
						project_owner: r.project_owner || '',
						project_owner_id: r.project_owner_id || ''
					});
						// Set client search field for editing - find client by ID or use project data
						const selectedClient = clients.find(c => c._id === r.related_client_id);
						if (selectedClient) {
							const isBusiness = selectedClient.type === 'business';
							const displayName = isBusiness ? 
								`${selectedClient.business_name} ${t('projects.businessSuffix')}` : 
								`${selectedClient.firstname} ${selectedClient.lastname} (${selectedClient.email})`;
							setClientSearch(displayName);
						} else if (r.related_client_id && r.client_name) {
							// If client not in dropdown but project has client data, show it
							const isBusiness = r.client_type === 'business';
							const displayName = isBusiness && r.business_name ? 
								`${r.business_name} ${t('projects.businessSuffix')}` : 
								`${r.client_name}`;
							setClientSearch(displayName);
						} else {
							setClientSearch('');
						}
						setEditingProjectId(r._id);
						setIsCreating(false);
						setShowNewProjectModal(true);
					}}>
						{t('common.edit')}
					</button>
					<button className="button button-small" onClick={e => {
						e.stopPropagation();
						deleteProject(r._id, r.project_name, r.client_name);
					}}>
						{t('common.delete')}
					</button>
				</div>
			)
		}] : [])
	];
	
	/* Render HTML */
	
	return (
		<div id="projects" className="row table_v2 projects">
			<div className="col p-0">
				<div className="x_panel chatbot-builder">
					<div className="header-menu d-flex justify-content-between align-items-center">
						<div className="header-title d-flex align-items-center">
							<h1>{t('projects.title')}</h1>
						</div>
					<div className="header-actions d-flex align-items-center">
						{user && user.role === 'admin' && (
						<div style={{ position: 'relative', marginRight: '10px' }}>
							<button
								className="button button-small"
								onClick={handleSyncProjectsToSheets}
								disabled={syncingProjects}
								style={{ position: 'relative', paddingRight: '35px' }}
							>
								{syncingProjects ? t('common.syncing') : t('projects.syncToSheets')}
							</button>
							<div
								style={{
									position: 'absolute',
									right: 0,
									top: 0,
									width: '30%',
									height: '100%',
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-end',
									paddingRight: '8px',
									zIndex: 1
								}}
								onClick={(e) => {
									e.stopPropagation();
									if (spreadsheetUrl) {
										window.open(spreadsheetUrl, '_blank');
									}
								}}
								onMouseEnter={(e) => {
									const icon = e.currentTarget.querySelector('i');
									if (icon) icon.style.opacity = '1';
								}}
								onMouseLeave={(e) => {
									const icon = e.currentTarget.querySelector('i');
									if (icon) icon.style.opacity = '0';
								}}
							>
								<i 
									className="fa-solid fa-external-link" 
									style={{ 
										fontSize: '12px', 
										opacity: 0,
										color: '#032097',
										transition: 'opacity 0.2s'
									}}
								></i>
							</div>
						</div>
						)}
						<div className="action action-search">
							<input
								type="text"
								placeholder={t('projects.search')}
								value={search}
								onChange={e=>{ setSearch(e.target.value); setCurrentPage(1); }}
							/>
							<i class="fa-solid fa-magnifying-glass"></i>
						</div>
						<div className="action action-addnew">
								<button className="button button-blue" onClick={openNewProject}>
									{t('common.addNew')}
								</button>
							</div>
						</div>
					</div>
					<div className="x_content">
						<div className="page-container d-flex flex-column">
							{loading ? <div>{t('common.loading')}</div> : (
							<>
								<div className= "page-header d-flex justify-content-between align-items-center">
									<div className="filter filter-status d-flex align-items-center">
										<StatusFilter statusFilter={statusFilter} onChange={setStatusFilter} statusCounts={statusCounts} userRole={user.role} t={t} />
									</div>
									<div className="filter d-flex align-items-center" style={{marginLeft: '20px', gap: '10px'}}>
									<select value={projectTypeFilter} onChange={(e) => {
										setProjectTypeFilter(e.target.value);
										setCurrentPage(1);
									}}>
										<option value="all">{t('common.allTypes')}</option>
										<option value="General Booking">{t('projects.typeGeneralBooking')}</option>
										<option value="Personal Booking">{t('projects.typePersonalBooking')}</option>
										<option value="admin">{t('projects.typeAdmin')}</option>
										<option value="lawyer">{t('projects.typeLawyer')}</option>
										<option value="hugo+">{t('projects.typeHugoPlus')}</option>
										<option value="quote">{t('projects.typeQuote')}</option>
										<option value="App">{t('common.typeApp')}</option>
										<option value="initiated inquiry">{t('common.typeInitiatedInquiry')}</option>
									</select>
									<select value={month} onChange={(e) => {
										setMonth(e.target.value);
										setCurrentPage(1);
									}}>
										<option value="">{t('common.allMonths')}</option>
										{year === '' ? (
											// When All Years is selected, show rolling 12 months with year
											generateRolling12Months(locale).map((m) => (
												<option key={m.value} value={m.value}>
													{m.label}
												</option>
											))
										) : (
											// When a specific year is selected, show months 1-12
											Array.from({ length: 12 }, (_, i) => (
												<option key={i + 1} value={i + 1}>
													{new Date(0, i).toLocaleString(locale === 'et' ? 'et-EE' : 'en-US', { month: 'long' })}
												</option>
											))
										)}
									</select>
									<select value={year} onChange={(e) => {
										setYear(e.target.value);
										setCurrentPage(1);
									}}>
										<option value="">{t('common.allYears')}</option>
										{Array.from({ length: Math.max(1, new Date().getFullYear() - 2025 + 1) }, (_, i) => {
											const yearOption = new Date().getFullYear() - i;
											return (
											<option key={yearOption} value={yearOption}>
												{yearOption}
											</option>
												);
										})}
									</select>
										{/* Partner filter - only show for admin users */}
										{user.role === 'admin' && (
											<select value={partnerFilter} onChange={(e) => {
												setPartnerFilter(e.target.value);
												setCurrentPage(1);
											}}>
												<option value="">{t('common.allPartners')}</option>
												{crmUsers.map((partner) => (
													<option key={partner.user_no} value={partner.user_no}>
														{partner.firstName} {partner.lastName} {partner.partner_company_name ? `(${partner.partner_company_name})` : ''}
													</option>
												))}
											</select>
										)}
										{/* CSV download button - only show for admin users */}
										{user.role === 'admin' && (
											<button onClick={handleDownloadCSV} className="button button-blue">
												{t('common.csv')}
											</button>
										)}
									</div>
									<div className='filter filter-pagesize d-flex justify-content-end align-items-center'>
									<select id="pageSize" value={sizePerPage} onChange={e=>{setSizePerPage(+e.target.value);setCurrentPage(1);}}>
										{[100,200,300,500].map(n=><option key={n} value={n}>{n}</option>)}
									</select>
									</div>
								</div>
								<div className="page-content nopaginate">
									<DataTablePaginated
									data          = {projects}
									columns       = {tableColumns}
									onTableChange = {handleTableChange}
									rowClasses    = {getRowClass}
									totalRowData  = {buildProjectTotalRowData(totals)}
									/>
								</div>
								<div className="page-footer d-flex justify-content-between align-items-center">
									<div className='filter filter-pagination d-flex align-items-center'>
										{Math.ceil(totalRecords / sizePerPage) > 0 && (
										<ReactPaginate
										containerClassName    = {'pagination'}
										activeClassName       = {'active'}
										previousLinkClassName = {'fa-regular fa-chevron-left'}
										nextLinkClassName     = {'fa-regular fa-chevron-right'}
										previousClassName     = {currentPage === 1 ? 'd-none' : 'previous'}
										nextClassName         = {currentPage === Math.ceil(totalRecords / sizePerPage) ? 'd-none' : 'next'}
										previousLabel         = {t('common.previous')}
										nextLabel             = {t('common.next')}
										breakLabel            = {t('common.ellipsis')}
										pageCount             = {Math.ceil(totalRecords/sizePerPage)}
										marginPagesDisplayed  = {2}
										pageRangeDisplayed    = {5}
										onPageChange          = {e=>setCurrentPage(e.selected+1)}
										forcePage             = {currentPage-1}
										/>
										)}
									</div>
									<div className='filter filter-pagesize d-flex justify-content-end align-items-center'>
									<select id="pageSize" value={sizePerPage} onChange={e=>{setSizePerPage(+e.target.value);setCurrentPage(1);}}>
										{[100,200,300,500].map(n=><option key={n} value={n}>{n}</option>)}
									</select>
									</div>
								</div>
							</>
							)}
						</div>
					</div>
				</div>
			</div>

			 {/* Modal for editing project_owner */}
			{showProjectModal && (
				<div className="modal project">
				<div className="modal-content" onClick={e => e.stopPropagation()}>
					<div className="modal-header">
						<span className="modalclose" onClick={() => setShowProjectModal(false)}>&times;</span>
						<h2>{t('projects.editOwnerTitle')}</h2>
					</div>
					<div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
						<div className="modal-form">
					<form onSubmit={handleProjectUpdate}>
						<div className="form-row col-full">
						<input
							type="text"
							placeholder={t('projects.projectOwner')}
							value={projectForm.project_owner}
							onChange={(e) => setProjectForm({ ...projectForm, project_owner: e.target.value })}
							required
						/>
						</div>
						<div className="form-row col-full">
						<input
							type="text"
							placeholder={t('projects.projectOwnerId')}
							value={projectForm.project_owner_id}
							onChange={(e) => setProjectForm({ ...projectForm, project_owner_id: e.target.value })}
							required
						/>
						</div>
						<div className="form-row col-full row-actions">
						<button type="button" className="button button-large" onClick={() => setShowProjectModal(false)}>{t('common.cancel')}</button>
						<button type="submit" className="button button-large button-blue">{t('common.update')}</button>
						</div>
					</form>
						</div>
					</div>
				</div>
				</div>
			)}
			
			{/* New Project Modal */}
			{showNewProjectModal && (
				<div className="modal project">
					<div className="modal-content" onClick={e => e.stopPropagation()}>
						<div className="modal-header">
							<span className="modalclose" onClick={() => setShowNewProjectModal(false)}>&times;</span>
							<h2>{isCreating ? t('projects.newProjectTitle') : t('projects.editProjectTitle')}</h2>
						</div>
						<div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
							{user?.role === 'lawyer' && isCreating && linkPrompt ? (
								<div className="project-link-client-modal">
									<div className="project-link-client-prompt client-modal-note">
										<i className="fa-solid fa-link" aria-hidden="true" />
										<div>
											<p>{t('projects.linkClientExistsIntro')}</p>
											<div className="project-link-client-modal-details">
												<p><strong>{t('clients.fieldFullName')}:</strong> {linkPrompt.displayName}</p>
												<p><strong>{t('clients.fieldEmail')}:</strong> {linkPrompt.email}</p>
											</div>
											<p>{t('projects.linkClientExistsQuestion')}</p>
											<div className="project-link-client-prompt-actions">
												<button type="button" className="button button-large" onClick={cancelLinkPrompt}>
													{t('common.no')}
												</button>
												<button type="button" className="button button-large button-blue" onClick={handleConfirmLinkAndContinue}>
													{t('common.yes')}
												</button>
											</div>
										</div>
									</div>
								</div>
							) : (
							<div className="modal-form">
							<form onSubmit={handleCreateProject}>
								<div className="form-row col-full">
									<label>{t('projects.projectName')} <span style={{color: 'red'}}>*</span></label>
									<input
										type="text"
										placeholder={t('projects.projectName').replace(':', '')}
										value={newProjectForm.project_name}
										onChange={e => setNewProjectForm({...newProjectForm, project_name: e.target.value})}
										required
									/>
								</div>
								<div className="form-row col-full">
									<label>{t('projects.projectDescription')}</label>
									<textarea
										placeholder={t('projects.projectDescription').replace(':', '')}
										value={newProjectForm.project_description}
										onChange={e => setNewProjectForm({...newProjectForm, project_description: e.target.value})}
									/>
								</div>
								{/* Project Type - Only show for admin users */}
								{user && user.role === 'admin' && (
									<div className="form-row col-full">
										<label>{t('projects.projectType')}</label>
										<select
											value={newProjectForm.project_type}
											onChange={e => setNewProjectForm({...newProjectForm, project_type: e.target.value})}
										>
											<option value="General Booking">{t('projects.typeGeneralBooking')}</option>
											<option value="personal">{t('projects.typePersonalBooking')}</option>
											<option value="admin">{t('projects.typeAdmin')}</option>
											<option value="lawyer">{t('projects.typeLawyer')}</option>
											<option value="hugo+">{t('projects.typeHugoPlus')}</option>
											<option value="quote">{t('projects.typeQuote')}</option>
											<option value="App">{t('common.typeApp')}</option>
											<option value="inquiry">{t('common.inquiry')}</option>
											<option value="initiated inquiry">{t('common.typeInitiatedInquiry')}</option>
										</select>
									</div>
								)}
								
								{/* Hide client selection for lawyer users when project is in waiting state during edit */}
								{!(user?.role === 'lawyer' && !isCreating && newProjectForm.status === 'waiting') && (
									isCreating && (user?.role === 'lawyer' || user?.role === 'admin') ? (
										<div className="form-row col-full project-lawyer-client-section">
											<label>{t('projects.clientLabel')} <span style={{color: 'red'}}>*</span></label>
											<div className="project-client-mode-toggle">
												<button
													type="button"
													className={`project-client-mode-chip${lawyerClientMode === 'existing' ? ' is-active' : ''}`}
													onClick={() => {
														setLawyerClientMode('existing');
														setLinkPrompt(null);
														setPendingProjectSubmit(false);
													}}
												>
													{t('projects.clientModeExisting')}
												</button>
												<button
													type="button"
													className={`project-client-mode-chip${lawyerClientMode === 'new' ? ' is-active' : ''}`}
													onClick={switchToNewClientMode}
												>
													{t('projects.clientModeNew')}
												</button>
											</div>

											{lawyerClientMode === 'existing' ? (
												<div className="client-search-container" style={{ position: 'relative' }}>
													<input
														type="text"
														placeholder={user?.role === 'lawyer' ? t('projects.searchClientLawyer') : t('projects.searchClient')}
														value={clientSearch}
														onChange={(e) => handleClientSearch(e.target.value)}
														onBlur={user?.role === 'lawyer' ? () => previewLinkByEmail(clientSearch) : undefined}
														onFocus={() => {
															if (clientSearch.trim() === '' && clients.length > 0) {
																setFilteredClients(clients);
																setShowClientDropdown(true);
															} else if (filteredClients.length > 0) {
																setShowClientDropdown(true);
															}
														}}
														style={{
															width: '100%',
															paddingRight: newProjectForm.related_client_id ? '30px' : '10px',
														}}
													/>
													{newProjectForm.related_client_id && (
														<button
															type="button"
															onClick={clearClientSelection}
															style={{
																position: 'absolute',
																right: '8px',
																top: '50%',
																transform: 'translateY(-50%)',
																background: 'none',
																border: 'none',
																cursor: 'pointer',
																color: '#999',
																fontSize: '16px',
															}}
															title={t('common.clearSelection')}
														>
															×
														</button>
													)}
													{showClientDropdown && (
														<div className="client-dropdown" style={{
															position: 'absolute',
															top: '100%',
															left: 0,
															right: 0,
															backgroundColor: 'white',
															border: '1px solid #ccc',
															borderTop: 'none',
															maxHeight: '250px',
															overflowY: 'auto',
															zIndex: 1000,
															boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
														}}>
															{user?.role === 'lawyer' && clientSearch.trim() === '' && (
																<div
																	className="client-option client-option--add-new"
																	onClick={switchToNewClientMode}
																	style={{
																		padding: '10px 12px',
																		cursor: 'pointer',
																		borderBottom: '1px solid #eee',
																		color: '#032097',
																		fontWeight: 500,
																	}}
																>
																	{t('projects.addNewClient')}
																</div>
															)}
															{filteredClients.slice(0, 20).map((client) => (
																<div
																	key={client._id}
																	className="client-option"
																	onClick={() => selectClient(client)}
																	style={{
																		padding: '10px 12px',
																		cursor: 'pointer',
																		borderBottom: '1px solid #eee',
																		backgroundColor: newProjectForm.related_client_id === client._id ? '#f0f8ff' : 'white',
																	}}
																>
																	{user?.role === 'admin' && client.type === 'business' ? (
																		<>
																			<div style={{ fontWeight: 'bold', marginBottom: '2px', pointerEvents: 'none', color: '#032097' }}>
																				{client.business_name}
																			</div>
																			<div style={{ fontSize: '11px', color: '#666', pointerEvents: 'none' }}>
																				{t('common.representative')}: {client.firstname} {client.lastname}
																			</div>
																			<div style={{ fontSize: '11px', color: '#666', pointerEvents: 'none' }}>
																				{client.email}
																			</div>
																		</>
																	) : (
																		<div style={{ fontWeight: 'bold', pointerEvents: 'none' }}>
																			{client.display_name || `${client.firstname} ${client.lastname}`.trim()}
																		</div>
																	)}
																</div>
															))}
															{filteredClients.length > 20 && (
																<div style={{ padding: '8px 12px', fontSize: '12px', color: '#666', textAlign: 'center', backgroundColor: '#f9f9f9' }}>
																	{t('common.showingFirst20')}
																</div>
															)}
															{filteredClients.length === 0 && clientSearch.trim() !== '' && (
																<div style={{ padding: '12px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
																	{t('common.noClientsFound', { query: clientSearch })}
																</div>
															)}
														</div>
													)}
												</div>
											) : (
												<div className="project-new-client-fields">
													<div className="client-modal-row client-modal-row--edit">
														<span className="client-modal-label">{t('clients.fieldFirstName')} <span style={{color: 'red'}}>*</span></span>
														<span className="client-modal-value client-modal-value--input">
															<input
																type="text"
																value={newClientForm.firstname}
																onChange={(e) => setNewClientForm({ ...newClientForm, firstname: e.target.value })}
																className={newClientErrors.name ? 'error' : ''}
															/>
														</span>
													</div>
													<div className="client-modal-row client-modal-row--edit">
														<span className="client-modal-label">{t('clients.fieldLastName')} <span style={{color: 'red'}}>*</span></span>
														<span className="client-modal-value client-modal-value--input">
															<input
																type="text"
																value={newClientForm.lastname}
																onChange={(e) => setNewClientForm({ ...newClientForm, lastname: e.target.value })}
																className={newClientErrors.name ? 'error' : ''}
															/>
														</span>
													</div>
													<div className="client-modal-row client-modal-row--edit">
														<span className="client-modal-label">{t('clients.fieldEmail')} <span style={{color: 'red'}}>*</span></span>
														<span className="client-modal-value client-modal-value--input">
															<input
																type="email"
																value={newClientForm.email}
																onChange={(e) => setNewClientForm({ ...newClientForm, email: e.target.value })}
																onBlur={user?.role === 'lawyer' ? () => previewLinkByEmail(newClientForm.email) : undefined}
																className={newClientErrors.email ? 'error' : ''}
															/>
														</span>
													</div>
													<div className="client-modal-row client-modal-row--edit">
														<span className="client-modal-label">{t('clients.fieldPhone')}</span>
														<span className="client-modal-value client-modal-value--input">
															<input
																type="text"
																value={newClientForm.phone}
																onChange={(e) => setNewClientForm({ ...newClientForm, phone: e.target.value })}
																className={newClientErrors.phone ? 'error' : ''}
															/>
														</span>
													</div>
													<div className="client-modal-row client-modal-row--edit">
														<span className="client-modal-label">{t('clients.fieldPersonalId')}</span>
														<span className="client-modal-value client-modal-value--input">
															<input
																type="text"
																value={newClientForm.pid}
																onChange={(e) => setNewClientForm({ ...newClientForm, pid: e.target.value })}
																className={newClientErrors.pid ? 'error' : ''}
															/>
														</span>
													</div>
													<div className="client-modal-row client-modal-row--edit">
														<span className="client-modal-label">{t('clients.fieldClientType')}</span>
														<span className="client-modal-value client-modal-value--input">
															<select
																value={newClientForm.type}
																onChange={(e) => setNewClientForm({ ...newClientForm, type: e.target.value })}
															>
																<option value="person">{t('clients.fieldPerson')}</option>
																<option value="business">{t('clients.fieldBusiness')}</option>
															</select>
														</span>
													</div>
													{newClientForm.type === 'business' && (
														<>
															<div className="client-modal-row client-modal-row--edit">
																<span className="client-modal-label">{t('clients.fieldBusinessName')} <span style={{color: 'red'}}>*</span></span>
																<span className="client-modal-value client-modal-value--input">
																	<input
																		type="text"
																		value={newClientForm.business_name}
																		onChange={(e) => setNewClientForm({ ...newClientForm, business_name: e.target.value })}
																		className={newClientErrors.business ? 'error' : ''}
																	/>
																</span>
															</div>
															<div className="client-modal-row client-modal-row--edit">
																<span className="client-modal-label">{t('clients.fieldBusinessAddress')} <span style={{color: 'red'}}>*</span></span>
																<span className="client-modal-value client-modal-value--input">
																	<input
																		type="text"
																		value={newClientForm.business_address}
																		onChange={(e) => setNewClientForm({ ...newClientForm, business_address: e.target.value })}
																		className={newClientErrors.business ? 'error' : ''}
																	/>
																</span>
															</div>
															<div className="client-modal-row client-modal-row--edit client-modal-row--last">
																<span className="client-modal-label">{t('clients.fieldRegistrationCode')} <span style={{color: 'red'}}>*</span></span>
																<span className="client-modal-value client-modal-value--input">
																	<input
																		type="text"
																		value={newClientForm.business_reg_code}
																		onChange={(e) => setNewClientForm({ ...newClientForm, business_reg_code: e.target.value })}
																		className={newClientErrors.business ? 'error' : ''}
																	/>
																</span>
															</div>
														</>
													)}
													{(newClientErrors.name || newClientErrors.email || newClientErrors.phone || newClientErrors.pid || newClientErrors.business || newClientErrors.general) && (
														<p className="project-new-client-error">
															{newClientErrors.name || newClientErrors.email || newClientErrors.phone || newClientErrors.pid || newClientErrors.business || newClientErrors.general}
														</p>
													)}
												</div>
											)}
										</div>
									) : (
										<div className="form-row col-full">
											<label>{t('projects.clientLabel')} <span style={{color: 'red'}}>*</span></label>
											<div className="client-search-container" style={{ position: 'relative' }}>
												<input
													type="text"
													placeholder={t('projects.searchClient')}
													value={clientSearch}
													onChange={(e) => handleClientSearch(e.target.value)}
													onFocus={() => {
														if (clientSearch.trim() === '' && clients.length > 0) {
															setFilteredClients(clients);
															setShowClientDropdown(true);
														} else if (filteredClients.length > 0) {
															setShowClientDropdown(true);
														}
													}}
													style={{
														width: '100%',
														paddingRight: newProjectForm.related_client_id ? '30px' : '10px',
													}}
													required={!newProjectForm.related_client_id}
												/>
												{newProjectForm.related_client_id && (
													<button
														type="button"
														onClick={clearClientSelection}
														style={{
															position: 'absolute',
															right: '8px',
															top: '50%',
															transform: 'translateY(-50%)',
															background: 'none',
															border: 'none',
															cursor: 'pointer',
															color: '#999',
															fontSize: '16px',
														}}
														title={t('common.clearSelection')}
													>
														×
													</button>
												)}
												{showClientDropdown && filteredClients.length > 0 && (
													<div className="client-dropdown" style={{
														position: 'absolute',
														top: '100%',
														left: 0,
														right: 0,
														backgroundColor: 'white',
														border: '1px solid #ccc',
														borderTop: 'none',
														maxHeight: '250px',
														overflowY: 'auto',
														zIndex: 1000,
														boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
													}}>
														{filteredClients.slice(0, 20).map((client) => (
															<div
																key={client._id}
																className="client-option"
																onClick={() => selectClient(client)}
																style={{
																	padding: '10px 12px',
																	cursor: 'pointer',
																	borderBottom: '1px solid #eee',
																	backgroundColor: newProjectForm.related_client_id === client._id ? '#f0f8ff' : 'white',
																}}
															>
																{client.type === 'business' ? (
																	<>
																		<div style={{ fontWeight: 'bold', marginBottom: '2px', pointerEvents: 'none', color: '#032097' }}>
																			{client.business_name}
																		</div>
																		<div style={{ fontSize: '11px', color: '#666', pointerEvents: 'none' }}>
																			{t('common.representative')}: {client.firstname} {client.lastname}
																		</div>
																		<div style={{ fontSize: '11px', color: '#666', pointerEvents: 'none' }}>
																			{client.email}
																		</div>
																	</>
																) : (
																	<>
																		<div style={{ fontWeight: 'bold', marginBottom: '2px', pointerEvents: 'none' }}>
																			{client.firstname} {client.lastname}
																		</div>
																		<div style={{ fontSize: '12px', color: '#666', pointerEvents: 'none' }}>
																			{client.email}
																		</div>
																	</>
																)}
															</div>
														))}
													</div>
												)}
											</div>
										</div>
									)
								)}
								
								{/* Client Type Selector - Hidden for all users */}
								{/* {user && user.role === 'admin' && (
									<div className="form-row col-full">
										<label>Client Type:</label>
										<select
											value={newProjectForm.client_type}
											onChange={e => {
												const newType = e.target.value;
												setNewProjectForm({
													...newProjectForm,
													client_type: newType
												});
											}}
											disabled={!!newProjectForm.related_client_id}
										>
											<option value="person">Person</option>
											<option value="business">Business</option>
										</select>
									</div>
								)} */}
								
								
								{/* Status - Admin-only editable field */}
								{user && user.role === 'admin' && (
									<div className="form-row col-full">
										<label>{t('projects.statusLabel')}</label>
										<select
											value={newProjectForm.status || 'waiting'}
											onChange={e => setNewProjectForm({...newProjectForm, status: e.target.value})}
										>
											<option value="waiting">{t('common.statusWaiting')}</option>
											<option value="active">{t('common.statusActive')}</option>
											<option value="finished">{t('common.statusFinished')}</option>
											<option value="cancelled">{t('common.statusCancelled')}</option>
										</select>
									</div>
								)}
								
								{/* Admin-only project owner selection */}
								{user && user.role === 'admin' && (
									<div className="form-row col-full">
										<label>{t('projects.projectOwnerLabel')}</label>
										<select
											value={newProjectForm.project_owner_id || ''}
											onChange={e => {
												const selectedUser = crmUsers.find(u => u.user_no === e.target.value);
												setNewProjectForm({
													...newProjectForm,
													project_owner_id: e.target.value,
													project_owner: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : ''
												});
											}}
											disabled={ownerChangeLoading}
										>
											<option value="">{t('projects.selectOwner')}</option>
											{crmUsers.map((user) => (
												<option key={user._id} value={user.user_no}>
													{user.firstName} {user.lastName} ({user.role}) - {user.email}
												</option>
											))}
										</select>
									</div>
								)}
								<div className="form-row col-full row-actions">
									<button type="button" className="button button-large" onClick={() => setShowNewProjectModal(false)}>{t('common.cancel')}</button>
									<button type="submit" className="button button-large button-blue">{isCreating ? t('common.create') : t('common.update')}</button>
								</div>
							</form>
							</div>
							)}
						</div>
					</div>
				</div>
			)}
			
			{/* Tooltip */}
			{
				tooltip.visible && (
					<div
					className='d-flex flex-column info-tooltip'
					style={{top: tooltip.y + 10, left: tooltip.x + 10}}
					dangerouslySetInnerHTML={{__html: tooltip.text}}
					/>
				)
			}
		</div>
	);
};
